import AutoAwesome from '@mui/icons-material/AutoAwesome'
import Block from '@mui/icons-material/Block'
import { Box, Stack, Theme, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import useLocalStorage from '@/hooks/useLocalStorage'
import {
  AUTO_ASSIGN_FILE_ACCEPT,
  AUTO_ASSIGN_FILE_EXTENSIONS,
  AUTO_ASSIGN_FILE_LABEL,
  AUTO_ASSIGN_MIME_TYPES,
  splitFilesByAcceptedType,
} from '@/utils/auto-assign-file-types'
import { showErrorSnackbar } from '@/utils/snackbars'
import DropZone from '../drop-zone/DropZone'
import FileUploadButton from '../upload-files/FileUploadButton'
import { useFileUploadContext } from '../upload-files/FileUploadProvider'

const darkThemeStyles = (theme: Theme) => ({
  '&:before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(circle at 20% 15%, ${alpha(
      theme.palette.primary.light,
      0.35,
    )}, transparent 55%)`,
    mixBlendMode: 'plus-lighter',
  },
})

export default function UploadCard() {
  const theme = useTheme()
  const { handleFilesSelectedToUpload } = useFileUploadContext()

  const [autoAssignCardVisibility] = useLocalStorage(
    'auto-assign-card-visibility',
    'show',
  )

  const isDarkTheme = theme.palette.mode === 'dark'

  // The drop zone screens by MIME type, which some platforms don't report. Names are
  // available here, so this is the check that actually decides what gets uploaded.
  function handleFilesSelected(files: FileList) {
    const { accepted, rejected } = splitFilesByAcceptedType(
      files,
      AUTO_ASSIGN_FILE_EXTENSIONS,
    )

    if (rejected.length) {
      showErrorSnackbar(
        `Auto Assign only accepts ${AUTO_ASSIGN_FILE_LABEL} files. Skipped ${rejected
          .map((file) => file.name)
          .join(', ')}.`,
        { autoHideDuration: 3500 },
      )
    }

    if (accepted.length) handleFilesSelectedToUpload(accepted)
  }

  if (autoAssignCardVisibility === 'hide') return null

  return (
    <Stack
      alignItems="center"
      spacing={4}
      sx={{
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <Box
        sx={(theme) => ({
          width: '100%',
          borderRadius: 4,
          p: 0.3,

          // Outline and background coloring for the border gradient
          background: `linear-gradient(130deg, ${alpha(
            theme.palette.primary.main,
            isDarkTheme ? 0.5 : 0.8,
          )}, ${alpha(
            theme.palette.secondary.main,
            isDarkTheme ? 0.45 : 0.65,
          )} 60%, ${alpha(theme.palette.primary.main, 0.85)})`,

          // These two are needed so the radial gradient doesn't bleed outside the card
          overflow: 'hidden',
          position: 'relative',

          // Radial gradient effect that enhances the border gradient
          ...(isDarkTheme ? darkThemeStyles(theme) : {}),
        })}
      >
        <DropZone
          onDrop={handleFilesSelected}
          acceptedMimeTypes={AUTO_ASSIGN_MIME_TYPES}
          rejectedOverlayChildren={
            <Stack
              alignItems="center"
              spacing={1}
              sx={{
                height: '100%',
                width: '100%',
                justifyContent: 'center',
                px: 3,
              }}
            >
              <Block sx={{ fontSize: 36 }} />
              <Typography variant="body2" textAlign="center">
                Auto Assign only accepts {AUTO_ASSIGN_FILE_LABEL} files
              </Typography>
            </Stack>
          }
          sx={{ borderRadius: 4 }}
        >
          <Box
            sx={(theme) => ({
              borderRadius: 3.5,
              p: 3,
              pb: 2,
              backdropFilter: isDarkTheme ? 'brightness(0)' : undefined,
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.background.paper,
                0.9,
              )} 0%, ${alpha(
                isDarkTheme ? theme.palette.background.paper : '#fff',
                isDarkTheme ? 0.6 : 0.8,
              )} 100%)`,
            })}
          >
            <Stack spacing={1} alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <AutoAwesome fontSize="large" />
                <Typography variant="h4" fontWeight={600}>
                  Auto Assign
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ maxWidth: 240 }}
              >
                Drop files here to automatically assign documents to projects
                with <b>Pixydocs AI</b>
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {AUTO_ASSIGN_FILE_LABEL} files only
              </Typography>

              <FileUploadButton
                multiple
                accept={AUTO_ASSIGN_FILE_ACCEPT}
                onFileUpload={handleFilesSelected}
              />
            </Stack>
          </Box>
        </DropZone>
      </Box>
    </Stack>
  )
}
